import {
  IonTabBar,
  IonTabButton,
  IonTabs,
  IonIcon,
  IonPopover,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
} from '@ionic/react';
import { menu, sync } from 'ionicons/icons';
import React, { Fragment, useEffect, useState } from 'react';
import { ReactComponent as PrayerIcon } from '../../assets/images/prayerLogo.svg';
import { ReactComponent as NewChatBubbleIcon } from '../../assets/images/chatbubbles-sharp-new.svg';
import './HeaderTabs.scss';
import { IonRouterOutlet} from '@ionic/react';
import { AppRoutes, AppRouteInfo, AppRouteKeys } from '../../routes/AppRoutes';
import { UserRoutes } from '../../routes/UserRoutes';
import UnregisteredUser from '../../data/UnregisteredUser';
import { Redirect, Route } from 'react-router-dom';
import PrayNow from '../../pages/PrayNowPage/PrayNowPage';
import { useAnalyticsLogging } from '../useAnalyticsLogging/useAnalyticsLogging';
import { useDispatch, useSelector } from 'react-redux';
import { SelectActiveReadingPlanRecord, SelectLastViewedURL, SelectNextUnreadURL, UpdateActivePlanByIdAsync, SelectReadingPlans, SelectBibleColorTheme, SelectBibleTabActive, SelectLastVersion } from '../../store/BibleStudySlice';
import { SelectActiveGroupId, SelectGroupIds, SelectGroups, UpdateActiveGroupId } from '../../store/FellowshipSlice';
import FellowshipSettingsService from '../../services/FellowshipSettingsService';
import BibleColorThemes, { BibleColorThemesValues } from '../../data/BibleColorThemes';
import { ReadingPlanCodes } from '../../data/ReadingPlans';

enum tabNames {
  home,
  bible,
  prayer,
  fellowship
}

const HeaderTabs: React.FC = () => {
  const dispatch = useDispatch();
  const [tabsHidden, setTabsHidden] = useState(false);
  const [showGroupChangeConfirmation, setShowGroupChangeConfirmation] = useState(false);
  const [groupChangeConfirmationEvent, setGroupChangeConfirmationEvent] = useState<Event | undefined>(undefined);
  const [popUpMessage, setPopUpMessage] = useState('');
  const nextUnreadSessionURL = useSelector(SelectNextUnreadURL);
  const lastViewBibleSessionURL = useSelector(SelectLastViewedURL);
  const activeGroupId = useSelector(SelectActiveGroupId);
  const [lastGroupId, setLastGroupId] = useState<string | undefined>(undefined);
  const activeReadingPlanRecord = useSelector(SelectActiveReadingPlanRecord);
  const [lastActivePlanId, setLastActivePlanId] = useState<number | undefined>(undefined)
  const userGroups = useSelector(SelectGroups);
  const userGroupsIds = useSelector(SelectGroupIds);
  const userBiblePlans = useSelector(SelectReadingPlans);
  const prayNowInfo = AppRouteInfo[AppRouteKeys.PrayNow]
  useAnalyticsLogging();
  const bibleColorTheme = useSelector(SelectBibleColorTheme);
  const [activeTab, setActiveTab] = useState<tabNames>(tabNames.home);
  const bibleTabActive = useSelector(SelectBibleTabActive);
  const lastSelectedVersion = useSelector(SelectLastVersion);

  useEffect(() => {
    if(bibleTabActive) {
      setActiveTab(tabNames.bible);
    } else {
      setActiveTab(tabNames.home);
    }
  
    return () => {}
  }, [bibleTabActive])
  

  useEffect(() => {
    if(activeGroupId && 
      activeReadingPlanRecord?.groupId && 
      userGroupsIds.length && 
      activeReadingPlanRecord?.groupId !== activeGroupId) {
      
      // If the group changed sync the reading plan to match the group
      if(activeGroupId !== lastGroupId && userBiblePlans?.find(plan => plan.groupId === activeGroupId)) {
        setShowGroupChangeConfirmation(true);
        setPopUpMessage(`Your active reading plan has been set to your active group's reading plan:`);
        const planId = userBiblePlans.find(plan => plan.groupId === activeGroupId)?.id;
        UpdateActivePlanByIdAsync(dispatch, userGroupsIds, planId);
      }

      // If the reading plan changed sync group to match reading plan
      if(activeReadingPlanRecord?.id !== lastActivePlanId && activeReadingPlanRecord?.groupId) {
        setShowGroupChangeConfirmation(true);
        setPopUpMessage(`Your active group has been set to your active reading plan's group:`);
        dispatch(UpdateActiveGroupId(activeReadingPlanRecord.groupId));
        FellowshipSettingsService.getFellowshipSettings().then(result => {
          if(result.Success) {
            const settingToUpdate =  result.Data!;
            settingToUpdate.defaultGroupId = activeReadingPlanRecord.groupId!;
            FellowshipSettingsService.updateFellowshipSettings(settingToUpdate);
          }
        });
      }
    }
    setLastGroupId(activeGroupId);
    setLastActivePlanId(activeReadingPlanRecord?.id);
  
    return () => {}
  }, [activeGroupId, activeReadingPlanRecord, userGroupsIds])

  const hideTabs = (hide: boolean) => {
    setTabsHidden(hide);
  }

  const getColorThemeClass = (activeSelectedTab: tabNames, bibleColorTheme?: BibleColorThemesValues) => {
    switch(bibleColorTheme) {
      case BibleColorThemes.darkMode:
        return activeSelectedTab === tabNames.bible ?'bible-header-dark-mode' : '';
      default:
        return '';
    }
  }

  return (
    <Fragment>
      <IonTabs className={getColorThemeClass(activeTab, bibleColorTheme)}
      >
        <IonRouterOutlet>
          <Route key="manual-render1" path={prayNowInfo.path} render={() => <PrayNow {...prayNowInfo} hideTabs={hideTabs}/>} exact /> 
          { AppRoutes(false, UnregisteredUser) }
          { UserRoutes(false, UnregisteredUser) }
          <Redirect exact from="/" to={AppRouteInfo[AppRouteKeys.Home].path}></Redirect>    
        </IonRouterOutlet>
        <IonTabBar slot="top" className={tabsHidden ? 'hidden' : ''}>
          
          <IonTabButton tab="home" href={AppRouteInfo[AppRouteKeys.Home].path} onClick={() => setActiveTab(tabNames.home)}>
            <IonIcon icon={menu} color={AppRouteInfo[AppRouteKeys.Home].themeColorName} size="large" />
          </IonTabButton>
          <IonTabButton tab="bible" href={lastViewBibleSessionURL ? lastViewBibleSessionURL : nextUnreadSessionURL ? nextUnreadSessionURL : `${AppRouteInfo[AppRouteKeys.Bible].rootPath}/${ReadingPlanCodes.bible}/1/${lastSelectedVersion}`} onClick={() => setActiveTab(tabNames.bible)}>
            <IonIcon icon={AppRouteInfo[AppRouteKeys.Bible].icon} className="bible-reading-header-tab" size="large" />
          </IonTabButton>
          <IonTabButton tab="prayer" href={AppRouteInfo[AppRouteKeys.Prayer].path} onClick={() => setActiveTab(tabNames.prayer)}>
            <PrayerIcon className="header-nav-icon-size prayer-header-tab" />
          </IonTabButton>
          <IonTabButton tab="fellowship" href={AppRouteInfo[AppRouteKeys.Fellowship].path} onClick={() => setActiveTab(tabNames.fellowship)}>
            { userGroups && activeGroupId && userGroups.hasOwnProperty(activeGroupId) && userGroups[activeGroupId].hasNewPosts ?
              <NewChatBubbleIcon className="header-fellowship-icon-size bounce-up fellowship-header-tab" /> :
              <IonIcon icon={AppRouteInfo[AppRouteKeys.Fellowship].icon} color={AppRouteInfo[AppRouteKeys.Fellowship].themeColorName} size="large" />
            }
          </IonTabButton>
        </IonTabBar>
      </IonTabs>
      <IonPopover
        mode="md"
        className="header-tab-pop-over-notice"
        dismissOnSelect={true}
        isOpen={showGroupChangeConfirmation}
        event={groupChangeConfirmationEvent}
        onDidDismiss={() => {
            setShowGroupChangeConfirmation(false); 
            setGroupChangeConfirmationEvent(undefined);
        }}
    >
      <IonCard>
        <IonCardHeader mode='ios'>
          <IonCardSubtitle>
            <IonIcon icon={sync}></IonIcon>&nbsp;
            SYNC GROUP AND PLAN
          </IonCardSubtitle>
        </IonCardHeader>
        <IonCardContent>{popUpMessage} <b><i>{userGroups && activeGroupId && userGroups.hasOwnProperty(activeGroupId) && userGroups[activeGroupId].group.name}</i></b></IonCardContent>
      </IonCard>
    </IonPopover>
    </Fragment>
  );
};

export default HeaderTabs;